import React, { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { useUserUid } from '../../hooks/useUserUid';

import {
  CORTEX_URL, VAF_URL, SUPABASE_URL, ENTROPY_URL,
} from '@/utils/environmentUtils';
import { stripSensitiveUrlParams } from '@/utils/posthog';

const applicationId = '2d52a226-3d6c-4ec8-859f-b77a33b29011';
const clientToken = 'pub0659587cdce54a0ed4687e965b49cf45';

if (process.env.NODE_ENV !== 'development') {
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: 'cap',
    env: process.env.REACT_APP_DEPLOY_ENV,
    version: process.env.APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      { match: (url: string) => url.startsWith(window.location.origin), propagatorTypes: ['tracecontext', 'datadog'] },
      { match: (url: string) => url.startsWith(CORTEX_URL), propagatorTypes: ['tracecontext', 'datadog'] },
      { match: (url: string) => url.startsWith(VAF_URL), propagatorTypes: ['tracecontext', 'datadog'] },
      { match: (url: string) => url.startsWith(SUPABASE_URL), propagatorTypes: ['tracecontext', 'datadog'] },
      { match: (url: string) => url.startsWith(ENTROPY_URL), propagatorTypes: ['tracecontext', 'datadog'] },
    ],
    beforeSend: (event) => {
      if (event.type === 'resource') {
        const { url } = event.resource;
        if (/posthog\.com|sentry\.io|singular\.net/.test(url)) {
          return false;
        }
      }
      // Same credentials the app-handoff bounce can leak into PostHog - strip them here too.
      //
      // Datadog derives the stored `url_query` and `url_hash` from `view.url`, so scrubbing
      // here is what keeps them out of RUM. It is also the ONLY thing that does: CAP's RUM is
      // the destination for the magic-link callback, whose fragment carries a live Supabase
      // session (see SENSITIVE_URL_PARAMS in utils/posthog).
      if (event.view?.url) {
        Object.assign(event.view, { url: stripSensitiveUrlParams(event.view.url) });
      }
      // The referrer is the same URL one navigation later. CAP moves off the callback screen
      // with a history.replace, and the view that follows carries the callback URL - fragment
      // and all - as its referrer, so scrubbing only `url` leaves the token in the next event.
      if (event.view?.referrer) {
        Object.assign(event.view, { referrer: stripSensitiveUrlParams(event.view.referrer) });
      }
      return true;
    },
    plugins: [reactPlugin({ router: false })],
  });
}

const DatadogInit: React.FC = () => {
  const { userUid } = useUserUid();

  useEffect(() => {
    if (userUid) {
      datadogRum.setUser({ id: userUid });
    } else {
      datadogRum.clearUser();
    }
  }, [userUid]);

  return null;
};

export default DatadogInit;
